/**
 * HQ's own widget TYPES for the Developer Dashboard — the web half of the
 * host-owned `hq.*` ids (the api half is the matching `DashboardDataSource`
 * per id in packages/api/src/services/dashboard/).
 *
 * WHY HQ REGISTERS ITS OWN IDS instead of reusing the stock ones: the stock
 * `dashboard.table` / `dashboard.kpiTile` config forms only offer `list` /
 * `metric`, and the api validates an instance's config against the SOURCE's
 * schema — so an HQ-specific knob (`onlyProblems`, `windowHours`, a KPI enum)
 * needs an HQ-owned type carrying ONE schema that both sides share verbatim.
 * That schema is `hqWidgetContract.json`, byte-identical to the api's copy and
 * pinned by hqWidgetContract.parity.test.ts. Nothing here hand-writes a
 * schema; every def reads it from the JSON so the two cannot drift.
 *
 * WHY STOCK RENDERERS: @intrasync/dashboard is a pure tool and HQ is a pure
 * host. HQ owns no widget UI — each def spreads a stock type (or goes through a
 * stock factory) and only changes the id, title, schema, default config, size
 * and navigation targets. Where a stock view lacks one knob its render is
 * wrapped, each with a HANDOFF note to the tool: `withSlotHeight` (the lists
 * have no maximum height), the stacked bar, the meeting lists' empty text and
 * `withEmptyText` (the table takes none). Rows are never tinted by the stock
 * table; severity on the three tables is a text column the api writes
 * (`severity`, `reason`) and worst-first colour lives in the attention band.
 *
 * TARGETS ARE ROUTE KEYS, NOT NAMES: the RouterStore map is keyed by
 * `makeRoute.key = name.replace(/ /g, "")`, and the registry refuses the WHOLE
 * batch when one declared target does not resolve. `HQ_ROUTE_KEYS` is the list
 * both the api (`route-keys.ts`) and these defs link to; hqWidgetTypes.test.ts
 * pins every key against the real routesList.
 *
 * TWO TYPES ARE ADMIN-ONLY: `hq.readiness` and `hq.activity` summarise the
 * admin-only System Health and Integration Errors pages, and the api refuses
 * their `dashboard-data.query` below admin (`DASHBOARD_ADMIN_WIDGET_TYPES`,
 * packages/api/src/services/dashboard-host.ts). `adminOnly: true` makes the
 * tool agree: for anyone else the palette leaves them out and a board leaves
 * their instances out (absent, and never queried), where they used to be two
 * tiles reading "could not load". The tool asks `isAdminPrincipal` of
 * `authStore.currentUser`, whose `tier` the api's user resolver fills from
 * `getUserTier`, the resolver its floor uses, so the two agree for every role
 * (owner, superadmin and custom admin-tier roles included). A third admin-only
 * type needs both sides; hqWidgetTypes.test.ts pins this side's two.
 */
import { createElement } from "react";
import { Box, Typography } from "@mui/material";
import type {
  GridSize,
  NavTarget,
  WidgetConfigSchema,
  WidgetKind,
  WidgetRenderProps,
  WidgetTypeDef,
} from "@intrasync/dashboard";
import {
  GRID_ROW_HEIGHT_PX,
  activityFeedType,
  attentionListType,
  barChartType,
  createKpiTileType,
  createWorkQueueType,
  dueListType,
  readinessListType,
  recentListType,
} from "@intrasync/dashboard";
import hqWidgetContract from "./hqWidgetContract.json";

/**
 * Every HQ route the Developer Dashboard links to, by RouterStore KEY (the
 * route name with spaces stripped). Mirrors packages/api/src/services/dashboard/route-keys.ts.
 */
export const HQ_ROUTE_KEYS = {
  ClientInstances: "ClientInstances",
  InternalContainers: "InternalContainers",
  Pipelines: "Pipelines",
  InstanceDetail: "InstanceDetail",
  DevOpsProject: "DevOpsProject",
  DevOpsOverview: "DevOpsOverview",
  DeploymentStatus: "DeploymentStatus",
  DockerHosts: "DockerHosts",
  PortainerConnections: "PortainerConnections",
  RegistryConnections: "RegistryConnections",
  DevOpsConnections: "DevOpsConnections",
  SystemHealth: "SystemHealth",
  IntegrationErrors: "IntegrationErrors",
  Email: "Email",
  LlmUsage: "LlmUsage",
  ClientRisk: "ClientRisk",
  ClientDetail: "ClientDetail",
  ReviewQueue: "ReviewQueue",
  MappingQueue: "MappingQueue",
  Tickets: "Tickets",
  Home: "Home",
  Clients: "Clients",
  MeetingDetail: "MeetingDetail",
  RoutingIssues: "RoutingIssues",
  InquiryQueue: "InquiryQueue",
} as const;

export type HqRouteKey = (typeof HQ_ROUTE_KEYS)[keyof typeof HQ_ROUTE_KEYS];

/** The shape of one entry of hqWidgetContract.json. */
interface ContractEntry {
  kind: WidgetKind;
  configSchema: WidgetConfigSchema;
  defaultConfig: Record<string, unknown>;
}

export type HqWidgetTypeId = keyof typeof hqWidgetContract;

/** The contract keyed by type id, typed for the defs below. */
export const HQ_WIDGET_CONTRACT = hqWidgetContract as Record<HqWidgetTypeId, ContractEntry>;

/** Every HQ widget type id, in the contract's (palette) order. */
export const HQ_WIDGET_TYPE_IDS = Object.keys(HQ_WIDGET_CONTRACT) as HqWidgetTypeId[];

const routeTargets = (...keys: HqRouteKey[]): NavTarget[] => keys.map((routeName) => ({ routeName }));

/** Every route key — what the attention band and the KPI tiles may link to. */
const ALL_ROUTE_TARGETS = routeTargets(...(Object.values(HQ_ROUTE_KEYS) as HqRouteKey[]));

const contractOf = (id: HqWidgetTypeId) => {
  const entry = HQ_WIDGET_CONTRACT[id];
  return { type: id, configSchema: entry.configSchema, defaultConfig: entry.defaultConfig };
};

type Def = WidgetTypeDef<Record<string, unknown>>;

/**
 * The "Add widget" palette group every HQ type is listed under (0.0.4 groups the
 * palette by `category`, host groups first, the tool's "Standard widgets" last).
 * The group header is why the titles no longer carry an "HQ · " prefix: it told
 * HQ's types apart in 0.0.3's flat alphabetical list, and under a header it is
 * noise — and a blank instance title falls back to the type title on the board.
 */
export const HQ_PALETTE_GROUP = "HQ";

/**
 * The grid's row gap in px from the theme's `sm` breakpoint up. The tool's
 * DashboardGrid passes `gap: { xs: 1, sm: GRID_GAP }` (GRID_GAP = 2), which MUI
 * resolves through `theme.spacing`: HQ keeps the 8 px unit, so 16 px from `sm`
 * up and 8 px below it (0.0.4 halved the phone's gap). Only the `sm`-up value
 * is pinned, because below `sm` no cell is `h` rows tall (see `slotHeightPx`).
 * Pinned here because `GRID_GAP` is not on the package index (only
 * `GRID_ROW_HEIGHT_PX` is), and a theme-derived value would need a hook the
 * render function cannot call.
 */
const GRID_ROW_GAP_PX = 16;

/**
 * Vertical px the widget frame spends around a render: the DashboardWidget
 * header row (the 28 px subtitle1 title beside a `small` Refresh IconButton)
 * plus CardContent's last-child bottom padding (its top is `pt: 0`). From
 * @intrasync/dashboard 0.0.4 widgets/chrome/DashboardWidget, measured under
 * HQ's theme on 2026-10-30. It depends on the width, because HQ's theme gives
 * every IconButton a 44 px touch floor below `md` (renderApp.tsx, H7):
 *   - `md` and up: header 12 + 30 + 12, foot 24 = 78 px;
 *   - `sm` to `md`: header 12 + 44 + 12, foot 24 = 92 px;
 *   - below `sm`, where 0.0.4 pads the card less: header 8 + 44 + 8, foot 12 = 72 px.
 * 82 assumed a 34 px button (the frame's is 5 + 20 + 5 = 30 px); it sits
 * between the first two and only needs to be close. On a desktop the bounded
 * list stops 4 px short of its slot and on a tablet it can stretch its row by
 * 10 px, both harmless, whereas an unbounded list is not (see `withSlotHeight`).
 */
const WIDGET_CHROME_PX = 82;

/**
 * The px a widget's render may occupy inside its grid slot: `h` rows of
 * `GRID_ROW_HEIGHT_PX`, the `h - 1` gaps between them, minus the frame's chrome.
 * That is the slot from `sm` up, where a cell spans `h` rows. Below `sm` a
 * single-column phone's rows are content height (0.0.4), so no cell is `h`
 * rows tall and this is only a cap: the list scrolls past it, and its card is
 * at most this plus the 72 px phone chrome.
 */
export const slotHeightPx = (size: GridSize): number =>
  size.h * GRID_ROW_HEIGHT_PX + (size.h - 1) * GRID_ROW_GAP_PX - WIDGET_CHROME_PX;

/**
 * Bound a stock render to its grid slot, scrolling inside it.
 *
 * WHY: the tool's grid is CSS grid with `gridAutoRows: minmax(72px, auto)`, so
 * a widget's `h` is a MINIMUM — content grows the row, and every cell sharing
 * that row grows with it. Measured 2026-10-26 on a real stack at 1440x900: the
 * 'Platform readiness' list (one row per capability, 1587 px tall) landed in
 * grid row 2 under `layout.order = 'worstFirst'` and pushed the KPI tiles
 * ~800 px down. The stock DataTableView bounds itself (TableContainer
 * maxHeight 420), so the tables are fine; ReadinessListView and the stock
 * RowListView (the activity feed, the meeting lists) are unbounded, so HQ
 * bounds those here.
 * Only the stock render output is wrapped: loading / error / unavailable /
 * empty chrome belongs to the frame and stays outside.
 *
 * HANDOFF to @intrasync/dashboard: offer a layout option so rows honour `h`
 * with inner scroll (or give the list views a `maxHeight` like the table has),
 * then delete this.
 */
/** Spread, not inline: Box's prop type lists no `data-*` attribute, so an inline literal trips the excess-property check. */
const SLOT_BOUND_TEST_ID = { "data-testid": "hq-slot-bound" };

export const withSlotHeight = (def: Def): Def => ({
  ...def,
  render: (p: WidgetRenderProps<Record<string, unknown>>) =>
    createElement(
      Box,
      { ...SLOT_BOUND_TEST_ID, sx: { maxHeight: slotHeightPx(p.size), overflowY: "auto", minWidth: 0 } },
      def.render(p),
    ),
});

/** Spread, not inline: Box and Typography list no `data-*` attribute, so an inline literal trips the excess-property check. */
const EMPTY_TABLE_TEST_ID = { "data-testid": "hq-table-empty" };

/** A result that was measured and has no rows: not loading, not failed. */
const isMeasuredEmpty = (result: WidgetRenderProps["result"]): boolean =>
  !!result && !result.unavailable && result.kind === "rows" && result.data.length === 0;

/**
 * A table's empty state in the table's own words.
 *
 * WHY: the stock table prints "No rows" whatever it is a table of, which on a problems-only table reads
 * like a fault and says nothing about the filter that produced it. The strict config schema has no
 * `emptyText` and a rows result carries no reason (the wire envelope is kind, data, asOf, rowCount,
 * partial), so the render words it from the config, as `meetingWorkEmptyText` does for the lists, which
 * the stock list does take. A result that is loading, unavailable or has rows is the stock table's,
 * untouched. What this cannot say is "none recorded" on a problems-only table: that needs the size of
 * the population, which no result field carries (the container tiles beside it say so).
 *
 * HANDOFF to @intrasync/dashboard: DataTableView should take an `emptyText` as RowListView does, then
 * delete this.
 */
export const withEmptyText = (def: Def, textFor: (config: Record<string, unknown>) => string | undefined): Def => ({
  ...def,
  render: (p: WidgetRenderProps<Record<string, unknown>>) => {
    const text = isMeasuredEmpty(p.result) ? textFor(p.config) : undefined;
    if (text === undefined) return def.render(p);
    return createElement(
      Typography,
      { ...EMPTY_TABLE_TEST_ID, variant: "body2", sx: { p: 2, textAlign: "center", color: "text.secondary" } },
      text,
    );
  },
});

/** The api's default window for the pipeline table (the contract's `windowHours`), for the empty text only. */
const PIPELINES_WINDOW_HOURS = 24;

const problemsOnly = (config: Record<string, unknown>): boolean => config.onlyProblems === true;

const clientInstancesEmptyText = (config: Record<string, unknown>): string =>
  problemsOnly(config) ? "No client instances with problems." : "No client instances recorded.";

const internalContainersEmptyText = (config: Record<string, unknown>): string =>
  problemsOnly(config) ? "No internal containers with problems." : "No internal containers recorded.";

const pipelinesEmptyText = (config: Record<string, unknown>): string => {
  const hours = typeof config.windowHours === "number" ? config.windowHours : PIPELINES_WINDOW_HOURS;
  return problemsOnly(config)
    ? `No pipelines with problems in the last ${hours} h.`
    : `No pipeline runs in the last ${hours} h.`;
};

const clientListEmptyText = (config: Record<string, unknown>): string | undefined => {
  switch (config.list) {
    case "atRisk":
      return "No client is red or yellow, so no account needs attention.";
    case "prospects":
      return "No client is marked as a prospect.";
    default:
      return undefined;
  }
};

/** "Needs attention": the ranked signal band across the top of the board. */
export const hqAttentionType: Def = {
  ...attentionListType,
  ...contractOf("hq.attention"),
  title: "Needs attention",
  category: HQ_PALETTE_GROUP,
  description:
    "What needs a human right now, worst first, each row leading to the screen that fixes it. " +
    "Choose a focus: the platform (containers, pipelines, integration errors, stale discovery), " +
    "operations (adds stuck work, tickets past their clock, stale updates) or sales " +
    "(clients at red health, customer containers down, overdue work, meetings waiting for a client).",
  source: "server",
  defaultSize: { w: 12, h: 2 },
  minSize: { w: 4, h: 2 },
  targets: ALL_ROUTE_TARGETS,
};

/**
 * One KPI figure per `metric`; the instance title is the label. w = 3, not 2:
 * the stock MetricTile's label is uppercase + noWrap, and at 1440 px a 2-wide
 * cell is 171 px, which truncates 'INTERNAL CONTAINERS DOWN' (measured 2026-10-26).
 */
export const hqKpiType: Def = createKpiTileType({
  ...contractOf("hq.kpi"),
  title: "KPI",
  category: HQ_PALETTE_GROUP,
  description:
    "One figure (containers down, pipelines failing, red or yellow clients, overdue action items, " +
    "tickets past their clock…) with its drill-through. " +
    "A metric that could not be read shows as failed, never as 0.",
  source: "server",
  defaultSize: { w: 3, h: 2 },
  minSize: { w: 3, h: 2 },
  targets: ALL_ROUTE_TARGETS,
});

/**
 * The three tables. `config.columns` is deliberately NOT offered: the stock
 * table derives its columns from the first row's key order, which the api
 * writes in the contract's column order — a `columns` default here would both
 * duplicate that order and be refused by the api's `additionalProperties:false`.
 */
export const hqClientInstancesType: Def = withEmptyText(
  createWorkQueueType({
    ...contractOf("hq.clientInstances"),
    title: "Client Instances",
    category: HQ_PALETTE_GROUP,
    description:
      "Every hosted client container with status, health, version and update state, worst first. " +
      "Read from the last reconcile sweep, never a live Portainer call.",
    source: "server",
    defaultSize: { w: 6, h: 5 },
    minSize: { w: 4, h: 3 },
    targets: routeTargets(HQ_ROUTE_KEYS.InstanceDetail, HQ_ROUTE_KEYS.ClientInstances, HQ_ROUTE_KEYS.ClientDetail),
    actions: [],
  }),
  clientInstancesEmptyText,
);

export const hqInternalContainersType: Def = withEmptyText(
  createWorkQueueType({
    ...contractOf("hq.internalContainers"),
    title: "Internal Containers",
    category: HQ_PALETTE_GROUP,
    description:
      "HQ's own containers (api, web, helpers) with status, health, version and update state, worst first.",
    source: "server",
    defaultSize: { w: 6, h: 5 },
    minSize: { w: 4, h: 3 },
    targets: routeTargets(HQ_ROUTE_KEYS.InstanceDetail, HQ_ROUTE_KEYS.InternalContainers),
    actions: [],
  }),
  internalContainersEmptyText,
);

export const hqPipelinesType: Def = withEmptyText(
  createWorkQueueType({
    ...contractOf("hq.pipelines"),
    title: "Pipelines",
    category: HQ_PALETTE_GROUP,
    description:
      "Running, queued and newest-completed builds per pipeline and branch over the window, " +
      "failures first. A project HQ could not read is a warning row, never silence.",
    source: "server",
    defaultSize: { w: 12, h: 5 },
    minSize: { w: 4, h: 3 },
    targets: routeTargets(HQ_ROUTE_KEYS.DevOpsProject, HQ_ROUTE_KEYS.Pipelines, HQ_ROUTE_KEYS.DevOpsOverview),
    actions: [],
  }),
  pipelinesEmptyText,
);

/**
 * One row per platform capability from capability-readiness; bounded to its
 * slot (see `withSlotHeight`). Admin-only, as the api's
 * DASHBOARD_ADMIN_WIDGET_TYPES (see the header).
 */
export const hqReadinessType: Def = withSlotHeight({
  ...readinessListType,
  ...contractOf("hq.readiness"),
  title: "Platform readiness",
  category: HQ_PALETTE_GROUP,
  description: "Each platform capability: ready, not ready, or could not be determined.",
  source: "server",
  adminOnly: true,
  defaultSize: { w: 4, h: 5 },
  minSize: { w: 3, h: 2 },
  targets: routeTargets(HQ_ROUTE_KEYS.SystemHealth),
});

/**
 * The merged activity feed (devops events, instance actions, integration
 * errors). Its rows carry `title, subtitle, at, actor, status` — the stock
 * feed renderer's default keys — so no `keys` config is needed. Bounded to
 * its slot (see `withSlotHeight`): the stock RowListView has no maxHeight.
 * Admin-only, as the api's DASHBOARD_ADMIN_WIDGET_TYPES (see the header).
 */
export const hqActivityType: Def = withSlotHeight({
  ...activityFeedType,
  ...contractOf("hq.activity"),
  title: "Activity",
  category: HQ_PALETTE_GROUP,
  description:
    "Newest first: Azure DevOps events, container actions and new integration errors from the last 24 hours.",
  source: "server",
  adminOnly: true,
  defaultSize: { w: 4, h: 5 },
  minSize: { w: 3, h: 2 },
  targets: routeTargets(
    HQ_ROUTE_KEYS.DevOpsProject,
    HQ_ROUTE_KEYS.DevOpsOverview,
    HQ_ROUTE_KEYS.InstanceDetail,
    HQ_ROUTE_KEYS.ClientInstances,
    HQ_ROUTE_KEYS.InternalContainers,
    HQ_ROUTE_KEYS.IntegrationErrors,
  ),
});

/**
 * Succeeded vs failed builds per day, STACKED. The stock bar chart reads
 * `config.stacked`, but `stacked` is a presentation choice the contract does
 * not expose (the api would refuse it under `additionalProperties:false`), so
 * the render pins it here rather than widening the schema.
 */
export const hqBuildsByDayType: Def = {
  ...barChartType,
  ...contractOf("hq.buildsByDay"),
  title: "Builds by day",
  category: HQ_PALETTE_GROUP,
  description: "Completed builds per day over the window: succeeded stacked on failed.",
  source: "server",
  defaultSize: { w: 4, h: 5 },
  minSize: { w: 3, h: 3 },
  targets: routeTargets(HQ_ROUTE_KEYS.Pipelines),
  render: (p) => barChartType.render({ ...p, config: { ...p.config, stacked: true } }),
};

/**
 * Client accounts as a table, `list` picking which: the accounts needing attention
 * (every client rated red or yellow, Red before Yellow, with the overdue items, the
 * containers down and the worst problem in words; the list id is still `atRisk`) or
 * the prospects. The same stock table as the instance and pipeline tables: its
 * columns are the keys the api writes, in order, and the header is the key with a
 * capital, so `config.columns` is not offered here either. Every client is listed to
 * every staff viewer, as the Client Risk page does.
 *
 * WIDTH: the table never wraps a cell, so it is as wide as its widest rows (688 px
 * for the five columns of the first list with the proof data) whatever its card is,
 * and a narrower card hides the last column (the Problem). A card gives its table
 * 96 x w - 48 px at 1440 and about 82 x w - 48 at 1280; both seeded boards give
 * these tables a row of their own (seed-manager-boards.ts).
 */
export const hqClientsType: Def = withEmptyText(
  createWorkQueueType({
    ...contractOf("hq.clients"),
    title: "Client lists",
    category: HQ_PALETTE_GROUP,
    description:
      "Client accounts as a table. Accounts needing attention: every client rated red or yellow, Red before Yellow, " +
      "with its overdue items, containers down and worst problem. Prospects: clients still marked prospect, with " +
      "their age, contacts and meetings.",
    source: "server",
    defaultSize: { w: 12, h: 5 },
    minSize: { w: 4, h: 3 },
    targets: routeTargets(HQ_ROUTE_KEYS.ClientDetail, HQ_ROUTE_KEYS.Clients, HQ_ROUTE_KEYS.ClientRisk),
    actions: [],
  }),
  clientListEmptyText,
);

/** The api's default window for the recent meetings (sources/meetingWork.ts), for the empty text only. */
const RECENT_MEETINGS_DAYS = 14;

/**
 * What an empty list of each kind means, said in that list's own words. The
 * strict schema offers no `emptyText`, and the stock lists' ("Nothing is due.",
 * "No activity yet.") would be wrong for four different lists, so the render
 * pins one per list, the way `hqBuildsByDayType` pins `stacked`.
 */
const meetingWorkEmptyText = (config: Record<string, unknown>): string | undefined => {
  switch (config.list) {
    case "myItems":
      return "You have no open follow-ups.";
    case "recent": {
      const days = typeof config.days === "number" ? config.days : RECENT_MEETINGS_DAYS;
      return `No client meetings in the last ${days === 1 ? "day" : `${days} days`}.`;
    }
    case "unmapped":
      return "Every meeting is tied to a client.";
    case "stuck":
      return "Nothing is stuck: every distributed item has an owner and can file an Azure DevOps work item.";
    default:
      return undefined;
  }
};

/**
 * The work that comes out of meetings, `list` picking which. Your own follow-ups
 * (items routed to you, plus those the analysis named for your e-mail address
 * even when routed on to someone else: the api's rule, sources/listReads.ts)
 * render as the stock DUE list (overdue and today coloured, an undated item says
 * "no date"); the rest as the stock RECENT list (the stuck work's chip is the
 * kind of problem). The row keys are the list renderer's own (`title`,
 * `subtitle`, `at`, `status`, `dueAt`), so no `keys` config is needed. Bounded to
 * its slot (see `withSlotHeight`): the stock RowListView has no maxHeight.
 */
export const hqMeetingWorkType: Def = withSlotHeight({
  ...recentListType,
  ...contractOf("hq.meetingWork"),
  title: "Meeting work",
  category: HQ_PALETTE_GROUP,
  description:
    "The work that comes out of meetings. Pick a list: your own open follow-ups (action items routed to you, or " +
    "named for your e-mail address even if they were routed on to someone else), recent client meetings, " +
    "meetings waiting for a client, or action items stuck without an owner or an Azure DevOps route, and why.",
  source: "server",
  defaultSize: { w: 6, h: 5 },
  minSize: { w: 3, h: 2 },
  targets: routeTargets(HQ_ROUTE_KEYS.MeetingDetail, HQ_ROUTE_KEYS.RoutingIssues),
  render: (p) => {
    const stock = p.config.list === "myItems" ? dueListType : recentListType;
    return stock.render({ ...p, config: { ...p.config, emptyText: meetingWorkEmptyText(p.config) } });
  },
});

/** Every HQ type, in the contract's order — what `installDashboardHost` registers. */
export const HQ_WIDGET_TYPES: readonly Def[] = [
  hqAttentionType,
  hqKpiType,
  hqClientInstancesType,
  hqInternalContainersType,
  hqPipelinesType,
  hqReadinessType,
  hqActivityType,
  hqBuildsByDayType,
  hqClientsType,
  hqMeetingWorkType,
];
